import {
  Box,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListSubheader,
  Popover,
} from '@mui/material';
import { colors, Tag, Typography } from '@sweep-io/sweep-design';
import { ListItem, OnCollapsedListItemClick } from '../../../../common/CollapseList';
import { TruncatedTextTooltip } from '../../../../common/TruncatedTextTooltip';
import { ObjectTypeLabelAndKeyTag } from '../../../../common/ObjectTypeLabelAndKey';
import { useMemo, useState } from 'react';
import { highlightMatch } from '../../../../../lib/highlightMatch';
import { SearchInput } from '../../../../common/SearchInput';
import {
  AdvancedFilter,
  AdvancedFilterItem,
} from '../../../../common/filters/advanced-filter/AdvancedFilter';
import { filterItemsBySearch } from '../../../../../lib/filterItemsBySearch';
import { FunnelType } from '../../../../../types/enums/FunnelType';
import { ConnectStepsListGroup } from './types';

export interface ConnectStepProps {
  anchorEl: HTMLElement | null;
  handleClose: () => any;
  stepGroups: ConnectStepsListGroup[];
  onItemClick: OnCollapsedListItemClick;
}

export const ConnectStep = ({
  anchorEl,
  handleClose,
  stepGroups,
  onItemClick,
}: ConnectStepProps) => {
  const isOpen = Boolean(anchorEl);
  const [search, setSearch] = useState('');

  const [selectedFilters, setSelectedFilters] = useState<(FunnelType | string)[]>([]);
  const selectableGroups = Object.values(stepGroups).filter(
    (v) => v.selectable || v.items.length,
  ).length;
  const selectableItems = Object.values(stepGroups).reduce(
    (sum, { items }) => sum + items.length,
    0,
  );
  const allVisibleItemsCount = selectableGroups + selectableItems;

  const showSearchField = allVisibleItemsCount >= 7;

  const filteredGroupsByFunnelType = useMemo(() => {
    return selectedFilters.length
      ? stepGroups.filter(
          (group) =>
            selectedFilters.includes(group.funnelType) ||
            (group.tag.type === 'other' && selectedFilters.includes(group.tag.tagText)),
        )
      : stepGroups;
  }, [stepGroups, selectedFilters]);

  const filteredGroups = useMemo(() => {
    return filteredGroupsByFunnelType.reduce((filteredElements: ConnectStepsListGroup[], group) => {
      //Check if string matches parent label
      const parentContainsString = filterItemsBySearch(
        [{ label: group.parentName ?? '' }],
        search,
        'label',
      );
      const labelContainsString = filterItemsBySearch([{ label: group.label }], search, 'label');

      const childrenContainsString = filterItemsBySearch<ListItem>(group.items, search, 'label');

      const tagContainsString = filterItemsBySearch(
        [{ label: group.tag.type === 'other' ? group.tag.tagText : group.tag.objectName }],
        search,
        'label',
      );

      if (
        parentContainsString.length ||
        childrenContainsString.length ||
        labelContainsString.length ||
        tagContainsString.length
      ) {
        filteredElements.push({
          ...group,
          label: group.label,
          items: parentContainsString.length ? group.items : childrenContainsString,
        });
      }

      return filteredElements;
    }, []);
  }, [filteredGroupsByFunnelType, search]);

  const advancedFilterItems = useMemo(() => {
    const funnelTypes = new Set(stepGroups.map((group) => group.funnelType));
    const items: AdvancedFilterItem[] = [];
    if (funnelTypes.has(FunnelType.SALESFORCE)) {
      items.push({
        label: 'Business processes',
        value: FunnelType.SALESFORCE,
      });
    }

    if (funnelTypes.has(FunnelType.THIRD_PARTY)) {
      items.push({
        label: '3rd party integrations',
        value: FunnelType.THIRD_PARTY,
      });
    }
    const metadataElementTypes = new Set(
      stepGroups
        .filter((group) => group.funnelType === FunnelType.METADATA_ELEMENT)
        .map((group) => (group.tag.type === 'other' ? group.tag.tagText : group.tag.objectName)),
    );
    if (metadataElementTypes.size > 0) {
      items.push(
        ...Array.from(metadataElementTypes).map((type) => ({
          label: type,
          value: type,
        })),
      );
    }

    return items;
  }, [stepGroups]);

  if (allVisibleItemsCount === 0) {
    return (
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        disablePortal
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              marginLeft: '10px',
              marginTop: '-2px',
              background: colors.grey[900],
              padding: '8px',
              borderRadius: '4px',
              color: colors.white,
            },
          },
        }}
      >
        <Typography variant="caption">
          No available destination steps. Steps can only be connected to a later step
        </Typography>
      </Popover>
    );
  }

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: -7,
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: -8,
      }}
      slotProps={{
        paper: {
          sx: {
            pb: 1,
            width: 640,
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
        <Typography variant="h5">Choose destination</Typography>
        {showSearchField && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              padding: '8px 12px',
              background: colors.grey[100],
              borderRadius: '4px',
              justifyContent: 'space-between',
            }}
          >
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              withFixedMagnifyingGlassIcon
              placeholder="Search"
              TextFieldProps={{
                sx: {
                  maxWidth: 360,
                },
              }}
              onClearButtonClick={() => setSearch('')}
              autoFocus
            />
            <AdvancedFilter
              texts={{ title: 'Show' }}
              selectedItems={selectedFilters}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              items={advancedFilterItems}
              onSelectedItemsChange={(items) => {
                setSelectedFilters(items as FunnelType[]);
              }}
            />
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 0, pb: 0 }}>
        <List
          sx={{
            position: 'relative',
            overflow: 'auto',
            maxHeight: 600,
            '& ul': { padding: 0, m: 0 },
            p: 0,
            pl: 2,
            pr: 2,
            '& li': {
              p: 0,
              m: 0,
            },
          }}
          subheader={<li />}
        >
          {filteredGroups
            .filter((stepGroup) => stepGroup.items.length || stepGroup.selectable)
            .map((stepGroup) => (
              <li key={`section-${stepGroup.value}`}>
                <ul style={{ padding: 0 }}>
                  <ListSubheader
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: 'white',
                      height: 40,
                    }}
                  >
                    <ListItemButton
                      onClick={() => {
                        if (stepGroup.selectable) {
                          onItemClick({ parentGroup: stepGroup });
                        }
                      }}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        '& > span:last-child': { display: 'none' },
                        '&:hover > span:last-child': {
                          display: 'inline',
                        },
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', gap: 1, height: 40, alignItems: 'center', flex: 1 }}
                      >
                        <TruncatedTextTooltip variant="body" color={colors.grey[900]}>
                          {highlightMatch(stepGroup.parentName ?? '', search)}
                        </TruncatedTextTooltip>
                        <Box>
                          {stepGroup.tag.type === 'object' && (
                            <ObjectTypeLabelAndKeyTag
                              label={highlightMatch(stepGroup.label, search)}
                              objectType={stepGroup.tag.objectName ?? stepGroup.value}
                            />
                          )}
                          {stepGroup.tag.type === 'other' && (
                            <Tag color={stepGroup.tag.tagColor} label={stepGroup.tag.tagText} />
                          )}
                        </Box>
                      </Box>
                      {stepGroup.selectable && (
                        <Typography variant="caption" color={colors.blue[500]}>
                          Connect
                        </Typography>
                      )}
                    </ListItemButton>
                  </ListSubheader>
                  {stepGroup.items.map((item) => (
                    <ListItemButton
                      key={`item-${stepGroup.value}-${item.value}`}
                      onClick={() => onItemClick({ item, parentGroup: stepGroup })}
                      sx={{
                        ml: '36px',
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        justifyContent: 'space-between',
                        '& > span:last-child': { display: 'none' },
                        '&:hover > span:last-child': {
                          display: 'inline',
                        },
                        '&:before': {
                          content: '""',
                          display: 'block',
                          position: 'absolute',
                          left: -16,
                          top: 0,
                          width: '1px',
                          height: '40px',
                          borderLeft: `1px solid ${colors.grey[300]}`,
                        },
                      }}
                    >
                      <Typography variant="body" color={colors.grey[900]}>
                        {highlightMatch(item.label, search)}
                      </Typography>
                      <Typography variant="caption" color={colors.blue[500]}>
                        Connect
                      </Typography>
                    </ListItemButton>
                  ))}
                </ul>
              </li>
            ))}
        </List>
      </DialogContent>
    </Popover>
  );
};
